import repository from '../../repositories/crm/crm.opportunity.repository';
import loginService from '../login.service';
import crmOpportunityActivityService from './crm.opportunity.activity.service';

import datetimeProvider from '../../../providers/datetime.provider';

import {
    negotiationStatusName,
    scheduleTexts,
} from '../../constant/crm/opportunity/crm.opportunity.schedule.constant';

const maxSchedulesPerOpportunity = 2;

const isLinkedSeller = (opportunity = {}) =>
    !!opportunity.seller?.id && loginService.getUserAuthentication()?.sellerId === opportunity.seller.id;

const isScheduledActivity = (activity = {}) => !!activity?.scheduleType;

const hasOpenSchedule = (opportunity = {}) =>
    typeof opportunity.hasOpenSchedule === 'boolean'
        ? opportunity.hasOpenSchedule
        : (opportunity.activities || []).some(activity => isScheduledActivity(activity) && !activity.concludedAt);

const isInNegotiation = (opportunity = {}) => opportunity.statusName === negotiationStatusName;

const hasContactResponseToday = (opportunity = {}, activity = null) =>
    typeof opportunity.hasContactResponseToday === 'boolean'
        ? opportunity.hasContactResponseToday
        : activity?.isContactResponse === true;

const isScheduleWindowOpen = (opportunity = {}, activity = null) =>
    isInNegotiation(opportunity) || hasContactResponseToday(opportunity, activity);

const hasReachedScheduleLimit = (opportunity = {}) =>
    Number(opportunity.scheduleCount || 0) >= maxSchedulesPerOpportunity;

const canScheduleFromDrawer = (opportunity = {}, activity = null) =>
    !opportunity.concludedAt
    && isScheduleWindowOpen(opportunity, activity)
    && !hasOpenSchedule(opportunity)
    && !hasReachedScheduleLimit(opportunity);

const canSchedule = (opportunity = {}) =>
    isLinkedSeller(opportunity) && canScheduleFromDrawer(opportunity);

const canCancelSchedule = (opportunity = {}) =>
    !opportunity.concludedAt
    && isLinkedSeller(opportunity)
    && opportunity.hasOpenSchedule === true
    && !opportunity.openSchedule?.isDue;

const getTodayDate = () => datetimeProvider.formatDateTimeBRT(new Date().toISOString(), 'YYYY-MM-DD');

const toLocalDateTime = (date, time) => (date && time ? `${date}T${time}` : null);

const getScheduledTime = (scheduledAt) => datetimeProvider.formatDateTimeBRT(scheduledAt, 'HH:mm');

const getScheduledWhen = (scheduledAt) => scheduledAt
    ? `${crmOpportunityActivityService.getDayLabel(datetimeProvider.formatDateTimeBRT(scheduledAt, 'YYYY-MM-DD'))} às ${getScheduledTime(scheduledAt)}`
    : '';

const shiftDate = (date, days) => {
    const shifted = new Date(`${date}T12:00:00Z`);
    shifted.setUTCDate(shifted.getUTCDate() + days);
    return shifted.toISOString().slice(0, 10);
}

const getRelativeDayLabel = (scheduledAt) => {
    const scheduledDate = datetimeProvider.formatDateTimeBRT(scheduledAt, 'YYYY-MM-DD');
    const today = getTodayDate();

    if (scheduledDate === today)
        return null;

    if (scheduledDate === shiftDate(today, -1))
        return scheduleTexts.yesterdayLabel;

    if (scheduledDate === shiftDate(today, 1))
        return scheduleTexts.tomorrowLabel;

    return datetimeProvider.formatDateTimeBRT(scheduledAt, 'DD/MM');
}

const getChipLabel = (activity = {}) =>
    scheduleTexts.getChipLabel(activity.scheduleType, getScheduledTime(activity.availableAt), getRelativeDayLabel(activity.availableAt));

const getWaitingNotice = (activity = {}) =>
    scheduleTexts.getWaitingNotice(activity.scheduleType, getScheduledTime(activity.availableAt), getRelativeDayLabel(activity.availableAt));

const getBanner = (openSchedule) => openSchedule?.activityId
    ? {
        title: scheduleTexts.getBannerTitle(openSchedule.scheduleType, getScheduledWhen(openSchedule.scheduledAt)),
    }
    : null;

const getErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

const findOperationConfig = () =>
    repository
        .findOperationConfig()
        .then(res => res.data || {});

const findScheduleConfig = () =>
    findOperationConfig().then(config => config.schedule || {});

const schedule = (opportunityId, { scheduleType, scheduledAt, concludeActivityId = null, origin = null }) =>
    repository
        .schedule(opportunityId, { scheduleType, scheduledAt, concludeActivityId, origin })
        .then(res => res.data?.value || {});

const cancelSchedule = (opportunityId, { origin = null } = {}) =>
    repository
        .cancelSchedule(opportunityId, { origin })
        .then(res => res.data?.value || {});

const concludeSchedule = (opportunityId, { outcome, conclusionMessage, nextScheduledAt = null, origin = null }) =>
    repository
        .concludeSchedule(opportunityId, { outcome, conclusionMessage, nextScheduledAt, origin })
        .then(res => res.data?.value || {});

const crmOpportunityScheduleService = {
    canSchedule,
    canScheduleFromDrawer,
    canCancelSchedule,
    hasOpenSchedule,
    hasReachedScheduleLimit,
    isScheduledActivity,
    isInNegotiation,
    isScheduleWindowOpen,
    getTodayDate,
    toLocalDateTime,
    getScheduledTime,
    getScheduledWhen,
    getChipLabel,
    getWaitingNotice,
    getBanner,
    getErrorMessage,
    findOperationConfig,
    findScheduleConfig,
    schedule,
    cancelSchedule,
    concludeSchedule,
};

export default crmOpportunityScheduleService;
